import { useEffect, useCallback, useRef } from "react";
import { queryClient } from "@/lib/queryClient";

const CRITICAL_KEYS = [
  ["/api/notifications/unread-count"],
  ["/api/matches/pending"],
  ["/api/matches/global/pending"],
];

const NORMAL_KEYS = [
  ["/api/notifications"],
  ["/api/friends/requests"],
  ["/api/friends/sent-pending"],
  ["/api/leagues"],
  ["/api/friends"],
];

const LOW_KEYS = [
  ["/api/activities"],
  ["/api/activities/feed"],
  ["/api/profile/level-history"],
  ["/api/profile/match-history"],
];

const CRITICAL_INTERVAL = 5_000;
const NORMAL_INTERVAL = 15_000;
const LOW_INTERVAL = 60_000;

function refetchKeys(keys: string[][]) {
  keys.forEach((key) => {
    queryClient.refetchQueries({ queryKey: key, type: "active" });
  });
}

function refetchAll() {
  refetchKeys(CRITICAL_KEYS);
  refetchKeys(NORMAL_KEYS);
  refetchKeys(LOW_KEYS);
}

function getPendingMatchIds(): Set<string> {
  const ids = new Set<string>();
  const leaguePending = queryClient.getQueryData<any[]>(["/api/matches/pending"]);
  const globalPending = queryClient.getQueryData<any[]>(["/api/matches/global/pending"]);
  if (leaguePending) leaguePending.forEach((m) => ids.add(m.id));
  if (globalPending) globalPending.forEach((m) => ids.add(m.id));
  return ids;
}

function cascadeInvalidateMatchChanges() {
  queryClient.invalidateQueries({ queryKey: ["/api/leagues"] });
  queryClient.invalidateQueries({
    predicate: (query) => {
      const key = query.queryKey;
      return Array.isArray(key) && key[0] === "/api/leagues" && key.length >= 2;
    },
  });
  queryClient.invalidateQueries({ queryKey: ["/api/profile/match-history"] });
  queryClient.invalidateQueries({ queryKey: ["/api/profile/level-history"] });
  queryClient.invalidateQueries({ queryKey: ["/api/auth/user"] });
  queryClient.invalidateQueries({ queryKey: ["/api/notifications"] });
  queryClient.invalidateQueries({ queryKey: ["/api/notifications/unread-count"] });
  queryClient.invalidateQueries({ queryKey: ["/api/activities"] });
  queryClient.invalidateQueries({ queryKey: ["/api/activities/feed"] });
}

export function useSmartPolling(enabled: boolean) {
  const prevPendingRef = useRef<Set<string> | null>(null);

  const handleVisibilityChange = useCallback(() => {
    if (document.visibilityState === "visible") {
      refetchAll();
    }
  }, []);

  useEffect(() => {
    if (!enabled) return;

    prevPendingRef.current = getPendingMatchIds();

    const checkForPendingChanges = () => {
      const currentIds = getPendingMatchIds();
      const prevIds = prevPendingRef.current;

      if (prevIds && prevIds.size > 0) {
        const disappeared = [...prevIds].some((id) => !currentIds.has(id));
        if (disappeared) {
          cascadeInvalidateMatchChanges();
        }
      }

      prevPendingRef.current = currentIds;
    };

    const criticalTimer = setInterval(() => {
      if (document.visibilityState === "visible") {
        refetchKeys(CRITICAL_KEYS);
        setTimeout(checkForPendingChanges, 1500);
      }
    }, CRITICAL_INTERVAL);

    const normalTimer = setInterval(() => {
      if (document.visibilityState === "visible") {
        refetchKeys(NORMAL_KEYS);
      }
    }, NORMAL_INTERVAL);

    const lowTimer = setInterval(() => {
      if (document.visibilityState === "visible") {
        refetchKeys(LOW_KEYS);
      }
    }, LOW_INTERVAL);

    document.addEventListener("visibilitychange", handleVisibilityChange);

    return () => {
      clearInterval(criticalTimer);
      clearInterval(normalTimer);
      clearInterval(lowTimer);
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [enabled, handleVisibilityChange]);
}
